// SF Symbols–style icons from design/Main.dc.html (24 px grid, 2 px rounded stroke, play filled).
import type { SVGProps } from 'react';

type IconProps = { size?: number } & Omit<SVGProps<SVGSVGElement>, 'width' | 'height'>;

const stroke = (size: number, width = 2, props: SVGProps<SVGSVGElement> = {}) => ({
  width: size,
  height: size,
  viewBox: '0 0 24 24',
  fill: 'none',
  stroke: 'currentColor',
  strokeWidth: width,
  strokeLinecap: 'round' as const,
  strokeLinejoin: 'round' as const,
  'aria-hidden': true,
  ...props,
});

export const PlayIcon = ({ size = 24, ...p }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" aria-hidden="true" {...p}>
    <path
      d="M8 5.5v13l11-6.5z"
      fill="currentColor"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinejoin="round"
    />
  </svg>
);

export const PauseIcon = ({ size = 24, ...p }: IconProps) => (
  <svg {...stroke(size, 3, p)}>
    <path d="M8.5 5.5v13M15.5 5.5v13" />
  </svg>
);

const Skip15 = ({ size = 34, forward, ...p }: IconProps & { forward?: boolean }) => (
  <svg {...stroke(size, 1.8, p)}>
    {forward ? (
      <>
        <path d="M20 12a8 8 0 1 1-2.4-5.7" />
        <path d="M20 3.5V8h-4.5" />
      </>
    ) : (
      <>
        <path d="M4 12a8 8 0 1 0 2.4-5.7" />
        <path d="M4 3.5V8h4.5" />
      </>
    )}
    <text
      x="12"
      y="15.2"
      textAnchor="middle"
      fontSize="7"
      fontWeight="800"
      fill="currentColor"
      stroke="none"
    >
      15
    </text>
  </svg>
);
export const Back15Icon = (p: IconProps) => <Skip15 {...p} />;
export const Forward15Icon = (p: IconProps) => <Skip15 {...p} forward />;

export const MoonIcon = ({ size = 24, filled, ...p }: IconProps & { filled?: boolean }) => (
  <svg {...stroke(size, 2.2, p)} {...(filled && { fill: 'currentColor', stroke: 'none' })}>
    <path d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z" />
  </svg>
);

export const LockIcon = ({ size = 24, ...p }: IconProps) => (
  <svg {...stroke(size, 2.2, p)}>
    <rect x="5" y="11" width="14" height="10" rx="3" />
    <path d="M8 11V8a4 4 0 0 1 8 0v3" />
  </svg>
);

export const CheckIcon = ({ size = 24, width = 2.4, ...p }: IconProps & { width?: number }) => (
  <svg {...stroke(size, width, p)}>
    <path d="M5 12.5l4.5 4.5L19 7.5" />
  </svg>
);

export const ChevronDownIcon = ({ size = 24, ...p }: IconProps) => (
  <svg {...stroke(size, 2.2, p)}>
    <path d="M6 9l6 6 6-6" />
  </svg>
);

export const ChevronRightIcon = ({ size = 16, ...p }: IconProps) => (
  <svg {...stroke(size, 2.4, p)}>
    <path d="M9 6l6 6-6 6" />
  </svg>
);

export const CloseIcon = ({ size = 14, ...p }: IconProps) => (
  <svg {...stroke(size, 3, p)}>
    <path d="M6 6l12 12M18 6L6 18" />
  </svg>
);

export const PersonIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...stroke(size, 2, p)}>
    <circle cx="12" cy="8" r="4" />
    <path d="M4.5 20a7.5 7.5 0 0 1 15 0" />
  </svg>
);

export const BellIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...stroke(size, 2, p)}>
    <path d="M6 16v-5a6 6 0 0 1 12 0v5l2 2H4z" />
    <path d="M10 21h4" />
  </svg>
);

export const GlobeIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...stroke(size, 2, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M3 12h18" />
    <path d="M12 3a14 14 0 0 1 0 18a14 14 0 0 1 0-18z" />
  </svg>
);

export const ChatIcon = ({ size = 22, ...p }: IconProps) => (
  <svg {...stroke(size, 2, p)}>
    <path d="M4 12a8 8 0 1 1 3.5 6.6L4 20l1.2-3.6A8 8 0 0 1 4 12z" />
  </svg>
);

export const InfoIcon = ({ size = 18, ...p }: IconProps) => (
  <svg {...stroke(size, 2.2, p)}>
    <circle cx="12" cy="12" r="9" />
    <path d="M12 8v5M12 16.5v.01" />
  </svg>
);

/** Ring spinner used inside buttons (Components · Buttons · Loading). */
export const Spinner = ({ size = 26, label }: { size?: number; label: string }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 40 40"
    role="img"
    aria-label={label}
    style={{ animation: 'spin 0.9s linear infinite' }}
  >
    <circle
      cx="20"
      cy="20"
      r="14"
      fill="none"
      stroke="currentColor"
      strokeOpacity="0.22"
      strokeWidth="4"
    />
    <path
      d="M20 6a14 14 0 0 1 14 14"
      fill="none"
      stroke="currentColor"
      strokeWidth="4"
      strokeLinecap="round"
    />
  </svg>
);

/** Equalizer bars for the playing row (Components · Story row · Playing). */
export const EqualizerIcon = ({ label, animate }: { label: string; animate: boolean }) => (
  <span
    role="img"
    aria-label={label}
    style={{
      width: 36,
      height: 36,
      display: 'flex',
      alignItems: 'flex-end',
      justifyContent: 'center',
      gap: 3,
      paddingBottom: 10,
      boxSizing: 'border-box',
      flexShrink: 0,
    }}
  >
    {[10, 16, 7, 13].map((h, i) => (
      <span
        key={i}
        style={{
          width: 3,
          height: h,
          borderRadius: 2,
          background: 'var(--accent)',
          transformOrigin: 'bottom',
          animation: animate ? `eq 900ms ${i * 120}ms ease-in-out infinite alternate` : 'none',
        }}
      />
    ))}
  </span>
);

// ── State illustrations (design/States.dc.html), 96-unit viewBox ──
export const IlloMoon = () => (
  <svg width="104" height="104" viewBox="0 0 96 96" aria-hidden="true">
    <path d="M56 16A22 22 0 1 0 78 48A24 24 0 0 1 56 16Z" fill="currentColor" fillOpacity="0.85" />
    <path
      d="M24 26Q24 30 28 30Q24 30 24 34Q24 30 20 30Q24 30 24 26Z"
      fill="currentColor"
      fillOpacity="0.6"
    />
    <path
      d="M34 62Q34 65 37 65Q34 65 34 68Q34 65 31 65Q34 65 34 62Z"
      fill="currentColor"
      fillOpacity="0.5"
    />
  </svg>
);
export const IlloOffline = ({ slash }: { slash: string }) => (
  <svg width="104" height="104" viewBox="0 0 96 96" aria-hidden="true">
    <path
      d="M26 68h44a14 14 0 0 0 0-28a20 20 0 0 0-38-4a16 16 0 0 0-6 32z"
      fill="currentColor"
      fillOpacity="0.8"
    />
    <path d="M30 80l40-56" stroke={slash} strokeWidth="6" strokeLinecap="round" />
  </svg>
);
export const IlloCloudMoon = ({ size = 104 }: { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 96 96" aria-hidden="true">
    <path d="M58 14A20 20 0 1 0 74 44A22 22 0 0 1 58 14Z" fill="currentColor" fillOpacity="0.35" />
    <path
      d="M28 74h40a14 14 0 0 0 0-28a20 20 0 0 0-38-4a16 16 0 0 0-2 32z"
      fill="currentColor"
      fillOpacity="0.8"
    />
  </svg>
);
export const IlloExpiredMoon = () => (
  <svg
    width="96"
    height="96"
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.6"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path
      d="M20 14.5A8 8 0 1 1 9.5 4a6.5 6.5 0 0 0 10.5 10.5z"
      fill="currentColor"
      fillOpacity="0.18"
    />
  </svg>
);
